import React, { useState } from 'react';
import styled from 'styled-components';
import { CreditCard, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useQuery } from 'react-query';
import { adminAPI } from '../services/api';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.surface};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.surface};
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

function Payments() {
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  const { data, isLoading, error } = useQuery(
    ['payments', statusFilter, providerFilter, userIdFilter],
    () =>
      adminAPI
        .getPayments({
          status: statusFilter || undefined,
          provider: providerFilter || undefined,
          userId: userIdFilter || undefined,
        })
        .then(res => res.data),
    {
      refetchInterval: 30000,
      onError: (err) => {
        console.error('Payments error:', err);
      }
    }
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fef3c7', color: '#d97706', icon: Clock },
      completed: { bg: '#d1fae5', color: '#059669', icon: CheckCircle },
      failed: { bg: '#fee2e2', color: '#dc2626', icon: XCircle },
      refunded: { bg: '#f3f4f6', color: '#6b7280', icon: XCircle },
    };
    const config = statusConfig[status] || { bg: '#f3f4f6', color: '#6b7280', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge bg={config.bg} color={config.color}>
        <Icon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU');
  };

  return (
    <Container>
      <Header>
        <Title>
          <CreditCard size={32} />
          Платежи
        </Title>
      </Header>

      <FiltersRow>
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">Все статусы</option>
          <option value="succeeded">Успешные</option>
          <option value="paid">Оплачено</option>
          <option value="completed">Завершено</option>
          <option value="pending">Ожидает</option>
          <option value="processing">Обработка</option>
          <option value="failed">Ошибка</option>
          <option value="canceled">Отменено</option>
          <option value="refunded">Возврат</option>
        </Select>
        <Input
          placeholder="Провайдер (yookassa, stripe...)"
          value={providerFilter}
          onChange={e => setProviderFilter(e.target.value)}
        />
        <Input
          placeholder="ID пользователя"
          value={userIdFilter}
          onChange={e => setUserIdFilter(e.target.value)}
        />
      </FiltersRow>

      {isLoading ? (
        <LoadingState>Загрузка платежей...</LoadingState>
      ) : error ? (
        <EmptyState>Ошибка загрузки данных</EmptyState>
      ) : !data || !data.payments || data.payments.length === 0 ? (
        <EmptyState>Платежей не найдено</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>ID</Th>
                  <Th>User ID</Th>
                  <Th>Provider</Th>
                  <Th>Сумма</Th>
                  <Th>Валюта</Th>
                  <Th>Кредиты</Th>
                  <Th>Статус</Th>
                  <Th>Транзакция</Th>
                  <Th>Дата</Th>
                </tr>
              </TableHeader>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <Td>{payment.id}</Td>
                    <Td>
                      #{payment.user_internal_id}{' '}
                      {payment.username ? `(${payment.username})` : ''}
                      {payment.user_tg_id ? <><br />TG: {payment.user_tg_id}</> : null}
                    </Td>
                    <Td>{payment.provider}</Td>
                    <Td>₽{Number(payment.amount_rub || 0).toLocaleString('ru-RU')}</Td>
                    <Td>{payment.currency || 'RUB'}</Td>
                    <Td>{Number(payment.credits || 0).toLocaleString('ru-RU')}</Td>
                    <Td>{getStatusBadge(payment.status)}</Td>
                    <Td>{payment.tx_id || '-'}</Td>
                    <Td>{formatDate(payment.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {data.total || 0}
          </div>
        </>
      )}
    </Container>
  );
}

export default Payments;
